import { Vector2 } from '@seddi/math';
import type { Scene } from '@seddi/scene';
import { type Object3D, Vector3 } from '@seddi/three';
import { Pointer, CURSORS } from '@seddi/ui-viewport-core';
import { isHotkeyPressed } from 'react-hotkeys-hook';

import type { Camera } from '../camera';
import type { Viewer, ViewerProps, ViewerState } from '../viewer';

type ICameraAux = {
  position: Vector3;
  direction: Vector3;
  forward: Vector3;
  right: Vector3;
  up: Vector3;
  speed: number;
};

export type CameraToolArgs = {
  camera: Camera;
  cameraControls: string;
  element: Viewer<ViewerProps, ViewerState>;
  pointer: Pointer;
};

type ICursor = { image: string; rotation?: number };

export type CameraToolPointer = Pointer & {
  setCursor: (cursor: ICursor) => void;
};

export type CameraToolUpdateArgs = {
  cursor?: ICursor;
  delta?: number;
  pointer?: CameraToolPointer;
  renderables?: Map<string, Object3D>;
  scene?: Scene;
};

export class CameraTool {
  camera: Camera;
  cameraAux: ICameraAux;
  cameraControls: string;
  element: Viewer<ViewerProps, ViewerState>;
  pointer: Pointer;

  static worldUp = new Vector3(0, 1, 0);

  /**
   * Creates a new 3D camera tool
   * @param args Constructor arguments that contains information about camera, camera controls, pointer and viewport
   */
  constructor({ camera, cameraControls, element, pointer }: CameraToolArgs) {
    this.camera = camera;
    this.cameraAux = {
      position: new Vector3(),
      direction: new Vector3(),
      forward: new Vector3(),
      right: new Vector3(),
      up: new Vector3(),
      speed: 1,
    };
    this.cameraControls = cameraControls;
    this.element = element;
    this.pointer = pointer;
    if (cameraControls === 'freelook') {
      camera.root.updateMatrixWorld();
      camera.getWorldPosition(camera.root.position);
      camera.position.set(0, 0, 0);
      pointer.requestPointerLock();
    }
  }

  /**
   * Resets the camera if free look is active then destroy pointer lock if exists
   */
  dispose(): void {
    const { camera, cameraControls, pointer } = this;
    if (cameraControls === 'freelook') {
      camera.reset();
      if (pointer.pointerlock) {
        pointer.pointerlock.destroy();
        delete pointer.pointerlock;
      }
    }
  }

  /**
   * Updates the cursor and conducts pointer actions with respect to the given arguments
   * @param args Object contains pointer and cursor information
   * @returns Flag indicating the camera needs update
   */
  onAnimationTick(args: CameraToolUpdateArgs): boolean {
    const { camera, cameraControls } = this;
    const { pointer, cursor: toolCursor } = args;
    let cursor = { image: CURSORS.VIEWPORT_DEFAULT };
    let needsUpdate = false;

    switch (cameraControls) {
      default:
      case 'freelook':
        cursor.image = CURSORS.VIEWPORT_DEFAULT;
        needsUpdate = this.onFreeLook(args);
        break;
      case 'orbit':
        if ((pointer.primaryDrag && isHotkeyPressed('alt')) || pointer.secondaryDrag) {
          cursor.image = CURSORS.GRABBING;
          if (pointer.movement.x || pointer.movement.y) {
            this.onOrbit({ pointer });
            needsUpdate = true;
          }
        } else if (isHotkeyPressed('space') || isHotkeyPressed('alt')) {
          cursor.image = CURSORS.GRAB;
        }
        break;
      case 'pan':
        if (isHotkeyPressed('space')) {
          cursor.image = CURSORS.GRAB;
        }
        if ((pointer.primaryDrag && isHotkeyPressed('space')) || pointer.secondaryDrag) {
          cursor.image = CURSORS.GRABBING;
          if (pointer.movement.x || pointer.movement.y) {
            this.onPan({ pointer });
            needsUpdate = true;
          }
        }
        break;
      case 'zoomin':
      case 'zoomout':
        if (isHotkeyPressed('space')) {
          cursor.image = cameraControls === 'zoomin' ? CURSORS.ZOOM_IN : CURSORS.ZOOM_OUT;
        }
        if ((pointer.primaryDown && isHotkeyPressed('space')) || pointer.secondaryDown) {
          this.onZoom({ direction: cameraControls === 'zoomin' ? -1 : 1 });
          needsUpdate = true;
        }
        break;
    }
    if (cameraControls === 'orbit' && isHotkeyPressed('space')) {
      cursor.image = pointer.primaryPressed ? CURSORS.GRABBING : CURSORS.GRAB;
      if ((pointer.movement.x || pointer.movement.y) && pointer.primaryPressed) {
        this.onPan({ pointer });
        needsUpdate = true;
      }
    }
    if (cameraControls === 'orbit' && pointer.tertiaryPressed && (pointer.movement.x || pointer.movement.y)) {
      this.onPan({ pointer });
      needsUpdate = true;
    }
    // Wheel zoom
    const isZooming =
      pointer.hover &&
      pointer.wheelEvent &&
      (Math.abs(pointer.wheelEvent.deltaX) > 0.5 || Math.abs(pointer.wheelEvent.deltaY) > 0.5);

    if ((cameraControls === 'orbit' || cameraControls === 'pan') && pointer.hover && isZooming) {
      const movementVector = new Vector2(pointer.wheelEvent.deltaX, pointer.wheelEvent.deltaY);

      const isZoomingOut =
        pointer.wheelEvent &&
        (movementVector.angle() >= 1.75 * Math.PI || movementVector.angle() <= 0.75 * Math.PI);

      const zoomMovement = isZoomingOut ? movementVector.length() : -movementVector.length();
      cursor.image = CURSORS.VIEWPORT_DEFAULT;
      const sensitivity = pointer.wheelEvent.ctrlKey ? 0.0012 : 0.0003;
      camera.incZoom(zoomMovement * sensitivity);
      camera.updateMatrixWorld();
      needsUpdate = true;
    }

    if (toolCursor && cursor.image === CURSORS.VIEWPORT_DEFAULT) {
      cursor = toolCursor;
    }

    // Update cursor
    pointer.setCursor(cursor);
    this.element.setCursor(cursor);

    return needsUpdate;
  }

  /**
   * Placeholder update function
   * @param _args Update arguments
   */
  onUpdate(_args: CameraToolUpdateArgs): void {}

  /**
   * Given the pointer is unlocked, updates the camera world matrix depending on the pointer movement, pointer wheel and keyboard inputs
   * @param args Object contains distance and pointer information
   * @returns Flag indicating the camera needs update
   */
  onFreeLook({ delta, pointer }: CameraToolUpdateArgs): boolean {
    if (!pointer.locked) {
      return false;
    }

    const { worldUp } = CameraTool;
    const {
      camera,
      cameraAux: { direction, forward, right, up },
    } = this;
    const { root } = camera;

    let needsUpdate = false;

    // Mouse Look
    if (pointer.movement.x || pointer.movement.y) {
      const sensitivity = 0.002;
      root.rotation.y -= pointer.movement.x * sensitivity;
      root.rotation.x -= pointer.movement.y * sensitivity;
      root.rotation.x = Math.min(Math.max(root.rotation.x, Math.PI * -0.5), Math.PI * 0.5);
      root.updateMatrixWorld();
      needsUpdate = true;
    }

    // Wheel sets speed
    if (pointer.wheel) {
      const sensitivity = 0.0003;
      const minSpeed = Math.log(0.02);
      const maxSpeed = Math.log(2);
      const range = maxSpeed - minSpeed;
      const logSpeed = Math.log(this.cameraAux.speed);
      let speed = (logSpeed - minSpeed) / range;
      speed = Math.min(Math.max(speed - pointer.wheel * sensitivity, 0), 1);
      this.cameraAux.speed = Math.exp(minSpeed + speed * range);
    }

    // Keyboard move
    if (
      isHotkeyPressed('W') ||
      isHotkeyPressed('S') ||
      isHotkeyPressed('A') ||
      isHotkeyPressed('D') ||
      isHotkeyPressed('space') ||
      isHotkeyPressed('shift')
    ) {
      camera.getWorldDirection(forward);
      forward.normalize();
      right.crossVectors(forward, worldUp).normalize();
      up.crossVectors(right, forward).normalize();
      direction.set(0, 0, 0);
      if (isHotkeyPressed('W')) {
        direction.add(forward);
      }
      if (isHotkeyPressed('S')) {
        direction.addScaledVector(forward, -1);
      }
      if (isHotkeyPressed('A')) {
        direction.addScaledVector(right, -1);
      }
      if (isHotkeyPressed('D')) {
        direction.add(right);
      }
      if (isHotkeyPressed('space')) {
        direction.add(up);
      }
      if (isHotkeyPressed('shift')) {
        direction.addScaledVector(up, -1);
      }
      root.position.addScaledVector(direction.normalize(), delta * this.cameraAux.speed);
      root.updateMatrixWorld();
      needsUpdate = true;
    }

    return needsUpdate;
  }

  /**
   * Sets camera rotation with respect to the given pointer movement and constant sensitivity, then updates the camera world matrix
   * @param args Object contains pointer information
   */
  onOrbit({ pointer }: { pointer: CameraToolPointer }): void {
    const sensitivity = 0.005;
    const {
      camera: { root },
    } = this;
    root.rotation.y -= pointer.movement.x * sensitivity;
    root.rotation.x -= pointer.movement.y * sensitivity;
    root.rotation.x = Math.min(Math.max(root.rotation.x, Math.PI * -0.5), Math.PI * 0.5);
    root.updateMatrixWorld();
  }

  /**
   * Sets camera position with respect to the given pointer movement and constant sensitivity, then updates the camera world matrix
   * @param args Object contains pointer information
   */
  onPan({ pointer }: { pointer: CameraToolPointer }): void {
    const sensitivity = 0.0015;
    const { camera } = this;
    camera.position.x -= pointer.movement.x * sensitivity;
    camera.position.y += pointer.movement.y * sensitivity;
    camera.updateMatrixWorld();
  }

  /**
   * Sets camera zoom with respect to the given direction, then updates the camera world matrix
   * @param args Object contains direction information
   */
  onZoom({ direction }: { direction: number }): void {
    const { camera } = this;
    camera.incZoom(0.03 * direction);
    camera.updateMatrixWorld();
  }

  /**
   * Checks if one of the pointer and keyboard inputs is navigating
   * @param pointer Pointer input data
   * @returns Flag indicating the inputs are in navigation
   */
  isNavigating(pointer: Pointer): boolean {
    return (
      isHotkeyPressed('space') ||
      pointer.tertiaryPressed ||
      isHotkeyPressed('alt') ||
      pointer.secondaryPressed
    );
  }
}
